import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import React, { FC, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useRecoilValue } from "recoil";

import { CustomForm } from "@/components/Form";
import { RegistrationHelpText } from "@/components/registration/RegistrationHelpText";
import { RegistrationPhases } from "@/components/registration/RegistrationPhases";
import { WheelInputField } from "@/components/selection/WheelInputField";
import { RegistrationActions, RegistrationFlowLayout as RegistrationTemplate } from "@/layouts/RegistrationFlowLayout";
import theme from "@/theme";
import { getDefaultBirthYearMenuScrollValue } from "@/utilities/options";
import { UserRegistrationsAccoutFormState } from "@/view_models/atoms";

import { focusFirstRegistrationError } from "../utils/stepFields";

interface RegisterBirthYearMonthPageProps {
  totalSteps: number;
  currentStep: number;
  onNext: () => void;
  onBack: () => void;
  validate: () => Promise<boolean>;
}

const useStyles = makeStyles(() => ({
  gridItem: {
    padding: theme.spacing(1),
    "& .MuiFormControl-root": { minWidth: 0 },
  },
  noticeBox: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1),
    borderRadius: 8,
    backgroundColor: theme.palette.semantic["surface/base"],
  },
  noticeText: {
    color: theme.palette.text.secondary,
    fontWeight: 400,
    lineHeight: 1.6,
    fontSize: theme.typography.body2.fontSize,
  },
  // Gridに残る下余白8pxと合わせ、入力枠から20px空ける。
  kanaHelper: { marginTop: 12 },
}));

const RegisterBirthYearMonthPage: FC<RegisterBirthYearMonthPageProps> = ({
  totalSteps,
  currentStep,
  onNext,
  onBack,
  validate,
}) => {
  const {
    control,
    formState: { errors },
    setError,
  } = useFormContext();
  const formData = useRecoilValue(UserRegistrationsAccoutFormState);
  // 生年月はpre_diagnosis（投資力診断）で入力済み・atomに保持されているため、通常はここで聞かない。
  // atomが空（仮登録離脱・別端末復帰等）の場合のみ、救済として入力設問を表示する。
  const isBirthAlreadyKnown = formData.birth_year != null && formData.birth_month != null;

  useEffect(() => {
    // スクロールを最上部へ
    window.scrollTo({ top: 0, behavior: "smooth" });
    // formDataが存在する場合に初期値を設定
    control.setValue("family_name", formData.family_name);
    control.setValue("family_name_kana", formData.family_name_kana);
    control.setValue("first_name", formData.first_name);
    control.setValue("first_name_kana", formData.first_name_kana);
    control.setValue("birth_year", formData.birth_year);
    control.setValue("birth_month", formData.birth_month);
  }, []);

  const classes = useStyles();

  const handleNext = async () => {
    if (!isBirthAlreadyKnown) {
      const year = control.getValues("birth_year");
      const month = control.getValues("birth_month");
      const today = new Date();
      if (year > today.getFullYear() || (year === today.getFullYear() && month > today.getMonth() + 1)) {
        setError("birth_year", { type: "manual", message: "過去の年を入力してください。" });
        setError("birth_month", { type: "manual", message: "過去の月を入力してください。" });
        focusFirstRegistrationError();
        return;
      }
    }
    if (await validate()) onNext();
  };

  // TODO ユーザー編集と共通して使えるようにする
  const currentYear = new Date().getFullYear();
  const defaultBirthYear = getDefaultBirthYearMenuScrollValue();
  const years = Array.from(new Array(80), (_val, index) => currentYear - index).reverse(); // Array of years from current year to 80 years ago
  const months = Array.from(new Array(12), (_val, index) => index + 1); // Array of months from 1 to 12

  return (
    <RegistrationTemplate
      phaseTags={<RegistrationPhases completed />}
      title="あなたについて教えてください"
      description="あともう少しで完了です"
      totalSteps={totalSteps}
      currentStep={currentStep}
    >
      <Box className={classes.noticeBox}>
        <Typography className={classes.noticeText} align="center">
          ※こちらの情報は許可しない限り非公開となります
        </Typography>
      </Box>
      <Box mt={3}>
        <Grid container spacing={2}>
          <Grid item xs={6} className={classes.gridItem}>
            <Controller
              name="family_name"
              control={control}
              defaultValue={formData.family_name || ""}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="姓"
                  type="text"
                  error={!!errors.family_name}
                  errorText={errors.family_name?.message}
                  placeHolder="山田"
                />
              )}
            />
          </Grid>
          <Grid item xs={6} className={classes.gridItem}>
            <Controller
              name="first_name"
              control={control}
              defaultValue={formData.first_name || ""}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="名"
                  type="text"
                  error={!!errors.first_name}
                  errorText={errors.first_name?.message}
                  placeHolder="太郎"
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6} className={classes.gridItem}>
            <Controller
              name="family_name_kana"
              control={control}
              defaultValue={formData.family_name_kana || ""}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="セイ（カナ）"
                  type="text"
                  error={!!errors.family_name_kana}
                  errorText={errors.family_name_kana?.message}
                  placeHolder="ヤマダ"
                />
              )}
            />
          </Grid>
          <Grid item xs={6} className={classes.gridItem}>
            <Controller
              name="first_name_kana"
              control={control}
              defaultValue={formData.first_name_kana || ""}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="メイ（カナ）"
                  type="text"
                  error={!!errors.first_name_kana}
                  errorText={errors.first_name_kana?.message}
                  placeHolder="タロウ"
                />
              )}
            />
          </Grid>
        </Grid>
        {/* 生年月はpre_diagnosisで入力済みの場合は非表示（atomが空の場合のみ救済表示） */}
        {!isBirthAlreadyKnown && (
          <Grid container spacing={2}>
            <Grid item xs={6} className={classes.gridItem}>
              <Controller
                name="birth_year"
                control={control}
                render={(field) => (
                  <WheelInputField
                    label="生まれた年（西暦）"
                    value={field.value == null ? "" : String(field.value)}
                    onChange={(value) => field.onChange(Number(value))}
                    onBlur={field.onBlur}
                    values={years}
                    unit="年"
                    initialValue={defaultBirthYear}
                    error={errors.birth_year?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} className={classes.gridItem}>
              <Controller
                name="birth_month"
                control={control}
                render={(field) => (
                  <WheelInputField
                    label="生まれた月"
                    value={field.value == null ? "" : String(field.value)}
                    onChange={(value) => field.onChange(Number(value))}
                    onBlur={field.onBlur}
                    values={months}
                    unit="月"
                    initialValue={1}
                    error={errors.birth_month?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        )}
      </Box>
      <RegistrationHelpText className={classes.kanaHelper}>
        フリガナは全角カタカナで入力してください
      </RegistrationHelpText>
      <RegistrationActions onNext={handleNext} onBack={onBack} />
    </RegistrationTemplate>
  );
};

export default RegisterBirthYearMonthPage;
